import { useState, useEffect, useContext } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useCooldown } from '@/hooks/useCooldown'
import { useAutoLogin } from '@/hooks/useAutoLogin'
import { ToastContext } from '@/context/ToastContext'
import { generateOtp, resendOtp, verifyOtp } from '@/services/otpService'
import { ERROR_MESSAGES, TOAST_MESSAGES, STEP_EMAIL, STEP_OTP, STORAGE_KEY } from './constants'
import { capitalize } from '@/utils/helpers'
import { getDexConfig } from '@/utils/getDexConfig'
import { getEnvAndDex } from '@/utils/getEnvAndDex'
import AuthLeftPanel from '@/components/AuthLeftPanel'
import EmailStep from './components/EmailStep'
import OtpStep from './components/OtpStep'
import LoginLoading from './components/LoginLoading'

const LoginPage = () => {
  const { dex } = getEnvAndDex()
  const { LogoComponent: Logo } = getDexConfig(dex)
  const [searchParams, setSearchParams] = useSearchParams()
  const { login } = useAuth()
  const navigate = useNavigate()
  const { addToastMessage } = useContext(ToastContext)

  const raw = searchParams.get('redirectTo') || '/submittals'
  const redirectTo =
    raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\') ? raw : '/submittals'

  const submittalMatch = redirectTo.match(/\/submittals\/([^/?#]+)/)
  const submittalId = submittalMatch?.[1] ?? null
  const consultantId = new URL(redirectTo, window.location.origin).searchParams.get('consultantId')

  // Restore step 2 from sessionStorage on refresh (direct login flow only)
  const [step, setStep] = useState(() => {
    if (submittalId) return STEP_EMAIL
    if (searchParams.get('step') !== 'otp') return STEP_EMAIL
    return sessionStorage.getItem(STORAGE_KEY) ? STEP_OTP : STEP_EMAIL
  })
  const [email, setEmail] = useState(() => {
    if (submittalId) return ''
    if (searchParams.get('step') !== 'otp') return ''
    return sessionStorage.getItem(STORAGE_KEY) ?? ''
  })

  const [otp, setOtp] = useState('')
  const [loading, setLoading] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [blocked, setBlocked] = useState(false)

  const [cooldown, startCooldown] = useCooldown()

  // Clean up ?step=otp from URL if email is missing from sessionStorage (orphaned param)
  useEffect(() => {
    if (
      !submittalId &&
      searchParams.get('step') === 'otp' &&
      !sessionStorage.getItem(STORAGE_KEY)
    ) {
      setSearchParams(
        prev => {
          prev.delete('step')
          return prev
        },
        { replace: true }
      )
    }
  }, [])

  // On refresh of the OTP step (direct login), re-call generateOtp to restore cooldown state
  useEffect(() => {
    if (submittalId || step !== STEP_OTP || !email) return

    const restore = async () => {
      try {
        await generateOtp(email, 'direct_login')
        startCooldown()
      } catch (err) {
        if (err?.response?.status === 425) {
          startCooldown(err.response.data?.retry_after ?? 60)
        } else if (err?.response?.status === 429) {
          setBlocked(true)
          addToastMessage(ERROR_MESSAGES.OTP_TOO_MANY_ATTEMPTS, 'error', 'dismiss')
        } else {
          addToastMessage(
            capitalize(err?.response?.data?.detail) || ERROR_MESSAGES.OTP_SEND_FAILED,
            'error',
            'dismiss'
          )
        }
      }
    }

    restore()
  }, [])

  const { loading: autoLoginLoading } = useAutoLogin(submittalId, {
    consultantId,
    onSuccess: ({ email: prefill, cooldown: seconds }) => {
      setEmail(prefill)
      setStep(STEP_OTP)
      startCooldown(seconds)
    },
    onBlocked: ({ email: prefill }) => {
      setEmail(prefill)
      setStep(STEP_OTP)
      setBlocked(true)
      addToastMessage(ERROR_MESSAGES.OTP_TOO_MANY_ATTEMPTS, 'error', 'dismiss')
    },
    onError: msg => addToastMessage(msg, 'error', 'dismiss'),
    onNotFound: () => {
      addToastMessage(ERROR_MESSAGES.SUBMITTAL_NOT_FOUND, 'error', 'dismiss')
      navigate('/login', { replace: true })
    },
  })

  const goToOtpStep = (emailValue, cooldownSeconds) => {
    sessionStorage.setItem(STORAGE_KEY, emailValue)
    setSearchParams(
      prev => {
        prev.set('step', 'otp')
        return prev
      },
      { replace: true }
    )
    setStep(STEP_OTP)
    startCooldown(cooldownSeconds)
  }

  const handleEmailSubmit = async e => {
    e.preventDefault()
    if (!email || typeof email !== 'string' || !email.trim()) {
      setEmailError(ERROR_MESSAGES.EMAIL_REQUIRED)
      return
    }
    setEmailError('')
    setLoading(true)
    try {
      const trimmedEmail = email.trim()
      await generateOtp(trimmedEmail, 'direct_login')
      goToOtpStep(trimmedEmail)
    } catch (err) {
      if (err?.response?.status === 425) {
        const retryAfter = err.response.data?.retry_after ?? 60
        goToOtpStep(email.trim(), retryAfter)
        addToastMessage(TOAST_MESSAGES.OTP_ALREADY_SENT, 'success', 'dismiss')
      } else {
        addToastMessage(
          capitalize(err?.response?.data?.detail) || ERROR_MESSAGES.OTP_SEND_FAILED,
          'error',
          'dismiss'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleOtpSubmit = async e => {
    e.preventDefault()
    if (otp.length !== 6) return
    setVerifyLoading(true)
    try {
      const data = await verifyOtp(email, otp)
      sessionStorage.removeItem(STORAGE_KEY)
      login({
        accessToken: data.token,
        refreshToken: data.refresh_token,
        email: data.email,
        name: data.name,
      })
      const dest = new URL(redirectTo, window.location.origin)
      dest.searchParams.delete('consultantId')
      navigate(dest.pathname + dest.search + dest.hash, { replace: true })
    } catch (err) {
      const status = err?.response?.status
      if (status === 429) {
        addToastMessage(ERROR_MESSAGES.OTP_TOO_MANY_ATTEMPTS, 'error', 'dismiss')
        setBlocked(true)
      } else {
        addToastMessage(
          capitalize(err?.response?.data?.detail) || ERROR_MESSAGES.OTP_VERIFY_FAILED,
          'error',
          'dismiss'
        )
      }
    } finally {
      setVerifyLoading(false)
    }
  }

  const handleResend = async () => {
    if (cooldown > 0) return
    setResendLoading(true)
    try {
      const data = await resendOtp(email, 'direct_login')
      startCooldown(data?.retry_after)
      addToastMessage(TOAST_MESSAGES.OTP_RESENT, 'success', 'dismiss')
    } catch (err) {
      if (err?.response?.status === 429) {
        addToastMessage(ERROR_MESSAGES.OTP_TOO_MANY_ATTEMPTS, 'error', 'dismiss')
        setBlocked(true)
      } else if (err?.response?.status === 425) {
        const retryAfter = err.response.data?.retry_after ?? 60
        startCooldown(retryAfter)
        addToastMessage(TOAST_MESSAGES.OTP_RECENTLY_SENT, 'success', 'dismiss')
      } else {
        addToastMessage(
          capitalize(err?.response?.data?.detail) || ERROR_MESSAGES.OTP_RESEND_FAILED,
          'error',
          'dismiss'
        )
      }
    } finally {
      setResendLoading(false)
    }
  }

  if (autoLoginLoading) return <LoginLoading />

  return (
    <div className="flex max-tablet-p:flex-col tablet-p:min-h-screen">
      <AuthLeftPanel />
      <div className="bg-white flex items-center shrink-0 w-[40%] py-10 px-25 max-tablet-p:justify-center max-tablet-p:items-start max-tablet-p:w-full max-tablet-p:py-10 max-tablet-p:px-5">
        <div className="max-w-100 flex flex-col w-full gap-10 max-tablet-p:gap-6">
          <div className="flex justify-center tablet-p:hidden">
            <Logo className="h-14 w-auto" />
          </div>
          {step === STEP_EMAIL && (
            <EmailStep
              email={email}
              onChange={e => {
                setEmail(e)
                setEmailError('')
              }}
              onSubmit={handleEmailSubmit}
              loading={loading}
              error={emailError}
            />
          )}
          {step === STEP_OTP && (
            <OtpStep
              email={email}
              otp={otp}
              onChange={val => setOtp(val)}
              onSubmit={handleOtpSubmit}
              onResend={handleResend}
              resendLoading={resendLoading}
              verifyLoading={verifyLoading}
              cooldown={cooldown}
              blocked={blocked}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default LoginPage
